/**
 * InsurerCatalogBoundary — frontière d'état pour les surfaces assureurs qui
 * n'utilisent PAS `InsurerMultiSelect` (selects natifs, boutons-puces)
 * (9.V.2c-2b).
 *
 * Centralise le rendu chargement / erreur+réessai / registre vide une seule
 * fois, en render-prop, pour éviter de répéter ces trois états dans chaque
 * picker. Ne rend `children(catalog)` qu'une fois le registre résolu.
 *
 * Note R1 : la valeur sélectionnée d'une surface vit dans l'état du parent,
 * jamais dans ce composant — masquer temporairement le widget pendant le
 * chargement/l'erreur ne supprime aucune donnée. Aucun filtre implicite ici.
 * `renderChildrenWhenEmpty` permet d'afficher les enfants même si le registre
 * est vide (utile quand des valeurs historiques doivent rester visibles).
 */

import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import type { InsurerCatalog } from "../lib/queries/insurers";

type Props = {
  catalog: InsurerCatalog;
  children: (catalog: InsurerCatalog) => ReactNode;
  /** Rendre les enfants même si aucun assureur actif (préservation historique). */
  renderChildrenWhenEmpty?: boolean;
};

export function InsurerCatalogBoundary({ catalog, children, renderChildrenWhenEmpty }: Props) {
  const { t } = useTranslation();

  if (catalog.isLoading) {
    return (
      <p className="text-sm text-slate-500" aria-live="polite" role="status">
        {t("common.insurers.loading")}
      </p>
    );
  }

  if (catalog.isError) {
    return (
      <div className="flex items-center gap-2" role="alert">
        <span className="text-sm text-red-700">{t("common.insurers.error")}</span>
        <button
          type="button"
          onClick={catalog.refetch}
          className="text-brand-700 hover:text-brand-800 focus-visible:ring-brand-500 rounded text-sm font-medium underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
        >
          {t("common.retry")}
        </button>
      </div>
    );
  }

  if (catalog.isEmpty && !renderChildrenWhenEmpty) {
    return (
      <p className="text-sm text-slate-500" role="status">
        {t("common.insurers.empty")}
      </p>
    );
  }

  return <>{children(catalog)}</>;
}
